import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Fab,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
} from '@mui/material';
import {
  CameraAlt as CameraIcon,
  FlipCameraAndroid as FlipCameraIcon,
  Close as CloseIcon,
  Check as CheckIcon,
  PictureAsPdf as PDFIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { capturePhoto, clearError, clearSuccess, clearCurrentPhoto, uploadPDF, clearCurrentPDF, uploadPDFToExternalAPI, clearSuccessAfterDelay, convertPhotoToPDF } from '../../store/slices/photoSlice';
import { useMobileLayout } from '../../hooks/useMobileLayout';
import { useTouchTarget } from '../../hooks/useTouchTarget';
import { useNavigate } from 'react-router-dom';
import Webcam from 'react-webcam';
import { CameraSettings, PDFOptions } from '../../types/photo';

interface PhotoCaptureProps {
  onClose?: () => void;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({ onClose }) => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { isSmallMobile, spacing, typography } = useMobileLayout();
  const touchConfig = useTouchTarget();
  const navigate = useNavigate();

    const photoState = useSelector((state: RootState) => state.photo);
  const { isCapturing, isUploadingPDF, error, success, isUploading, currentPDF, currentPhoto } = photoState;

  const [cameraSettings, setCameraSettings] = useState<CameraSettings>({
    facingMode: 'environment',
    width: 3840, // 4K resolution for best OCR quality
    height: 2160, // 4K resolution for best OCR quality
    aspectRatio: 16 / 9,
    quality: 0.95, // High quality for OCR
    focusMode: 'continuous', // Continuous focus for document scanning
    exposureMode: 'continuous', // Continuous exposure for consistent lighting
    whiteBalanceMode: 'continuous' // Continuous white balance for consistent colors
  });

  const [isCameraReady, setIsCameraReady] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showPDFUploaded, setShowPDFUploaded] = useState(false);
  const [showPhotoCaptured, setShowPhotoCaptured] = useState(false);
  const [continuePressed, setContinuePressed] = useState(false);
  const webcamRef = useRef<Webcam>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);



  // Handle camera ready state
  const handleUserMedia = useCallback(() => {
    setIsCameraReady(true);
  }, []);

  // Handle camera errors
  const handleUserMediaError = useCallback((error: string | DOMException) => {
    console.error('Camera error:', error);
    dispatch(clearError());
  }, [dispatch]);

  // Auto-clear success state after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        dispatch(clearSuccessAfterDelay());
        setContinuePressed(false);
      }, 5000); // Clear after 5 seconds
      
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  // Handle close
  const handleClose = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      // If no onClose prop, navigate back but ensure we don't go to photo tab
      navigate(-1);
    }
  }, [onClose, navigate]);

  // Handle cancel (close without capturing)
  const handleCancel = useCallback(() => {
    // Clear any current photo/PDF and close
    dispatch(clearCurrentPhoto());
    dispatch(clearCurrentPDF());
    setShowPDFUploaded(false);
    setShowPhotoCaptured(false);
    setShowCamera(false);
    setContinuePressed(false); // Reset continue button state
    handleClose();
  }, [dispatch, handleClose]);

  // Handle camera capture
  const handleCameraCapture = useCallback(async () => {
    if (!webcamRef.current) return;

    try {
      const result = await dispatch(capturePhoto()).unwrap();
      // Show photo captured interface instead of immediately closing
      setShowPhotoCaptured(true);
      setShowCamera(false); // Hide camera interface when photo is captured
    } catch (error) {
      console.error('Photo capture error:', error);
      // Error is handled by the slice
    }
  }, [dispatch]);

  // Handle PDF upload
  const handlePDFUpload = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await dispatch(uploadPDF(file)).unwrap();
      // Show PDF uploaded interface
      setShowPDFUploaded(true);
    } catch (error) {
      console.error('PDF upload error:', error);
      // Error is handled by the slice
    }
  }, [dispatch]);

  // Handle PDF upload button click
  const handlePDFUploadClick = useCallback(() => {
    setContinuePressed(false); // Reset continue button state
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Handle proceed with uploaded PDF
  const handleProceedWithPDF = useCallback(async () => {
    if (continuePressed) return; // Prevent multiple submissions
    
    setContinuePressed(true);
    try {
      // Upload to external API
      await dispatch(uploadPDFToExternalAPI()).unwrap();
      
      // Show success and close after delay
      setTimeout(() => {
        handleClose();
      }, 3000);
    } catch (error) {
      console.error('Error processing PDF:', error);
      setContinuePressed(false); // Reset on error
    }
  }, [dispatch, continuePressed, handleClose]);

  // Handle proceed with captured photo
  const handleProceedWithPhoto = useCallback(async () => {
    if (continuePressed) return; // Prevent multiple submissions
    
    setContinuePressed(true);
    try {
      // Convert photo to PDF first
      const pdfOptions: PDFOptions = {
        orientation: 'portrait',
        format: 'a4',
        margin: 10,
        includeMetadata: true,
        dpi: 300,
        imageQuality: 0.95,
        compression: 'jpeg'
      };
      
      const result = await dispatch(convertPhotoToPDF(pdfOptions)).unwrap();
      
      if (result) {
        // Upload to external API
        await dispatch(uploadPDFToExternalAPI()).unwrap();
        
        // Show success and close after delay
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    } catch (error) {
      console.error('Error processing photo:', error);
      setContinuePressed(false); // Reset on error
    }
  }, [dispatch, continuePressed, handleClose]);

  // Handle camera button click
  const handleCameraClick = useCallback(() => {
    setContinuePressed(false); // Reset continue button state
    setShowCamera(true);
    setShowPDFUploaded(false);
    setShowPhotoCaptured(false);
  }, []);

  // Handle back from camera
  const handleBackFromCamera = useCallback(() => {
    setShowCamera(false);
    setIsCameraReady(false);
    setShowPDFUploaded(false);
    setShowPhotoCaptured(false);
  }, []);

  // Flip camera
  const handleFlipCamera = useCallback(() => {
    setCameraSettings(prev => ({
      ...prev,
      facingMode: prev.facingMode === 'user' ? 'environment' : 'user',
    }));
  }, []);

  // Clear messages
  const handleClearError = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleClearSuccess = useCallback(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearError());
      dispatch(clearSuccess());
      setShowPDFUploaded(false);
      setShowPhotoCaptured(false);
      setShowCamera(false);
    };
  }, [dispatch]);

  // If camera is active, show camera interface
  if (showCamera) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400, // Consistent with main PhotoCapture
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 2,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.7), transparent)',
          }}
        >
          <IconButton
            onClick={handleBackFromCamera}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h6"
              sx={{
                color: 'white',
                fontWeight: 'bold',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                marginBottom: 0.5,
              }}
            >
              Pořídit doklad
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.8)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                display: 'block',
              }}
            >
              Fotografie bude rozpoznána a převedena na fakturu
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(0,255,0,0.9)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                display: 'block',
                fontWeight: 'bold',
                marginTop: 0.5,
              }}
            >
              ✓ Vysoká kvalita pro OCR
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                display: 'block',
                fontSize: '0.7rem',
                marginTop: 0.5,
              }}
            >
              Automaticky se zavře po pořízení
            </Typography>
          </Box>

          <IconButton
            onClick={handleFlipCamera}
            sx={{
              color: 'white',
              backgroundColor: 'rgba(0,0,0,0.3)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.5)',
              },
            }}
          >
            <FlipCameraIcon />
          </IconButton>
        </Box>

        {/* Camera View */}
        <Box
          sx={{
            flex: 1,
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            screenshotQuality={0.95} // High quality screenshot for OCR
            videoConstraints={{
              facingMode: cameraSettings.facingMode,
              width: { 
                ideal: cameraSettings.width,
                min: 1920, // Minimum HD resolution for OCR
                max: 3840  // Support up to 4K
              },
              height: { 
                ideal: cameraSettings.height,
                min: 1080, // Minimum HD resolution for OCR
                max: 2160  // Support up to 4K
              },
              aspectRatio: { ideal: cameraSettings.aspectRatio },
              frameRate: { ideal: 30, min: 15 } // Smooth frame rate for better capture
            }}
            onUserMedia={handleUserMedia}
            onUserMediaError={handleUserMediaError}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />

          {/* Camera Overlay */}
          {isCameraReady && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                height: '60%',
                border: '2px solid rgba(255,255,255,0.8)',
                borderRadius: 2,
                pointerEvents: 'none',
              }}
            />
          )}

          {/* Loading Overlay */}
          {!isCameraReady && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CircularProgress size={60} sx={{ color: 'white' }} />
              <Typography variant="h6" sx={{ color: 'white' }}>
                Načítání kamery...
              </Typography>
            </Box>
          )}
        </Box>

        {/* Bottom Controls */}
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 3,
            background: 'linear-gradient(to top, rgba(0,0,0,0.7), transparent)',
          }}
        >
          <Fab
            onClick={handleCameraCapture}
            disabled={!isCameraReady || isCapturing}
            sx={{
              width: 80,
              height: 80,
              backgroundColor: 'white',
              color: 'black',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)',
              },
              '&:disabled': {
                backgroundColor: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            {isCapturing ? (
              <CircularProgress size={40} />
            ) : (
              <CameraIcon sx={{ fontSize: 40 }} />
            )}
          </Fab>
          
          {/* Capture feedback text */}
          {isCapturing && (
            <Typography
              variant="caption"
              sx={{
                color: 'white',
                textAlign: 'center',
                marginTop: 2,
                textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              }}
            >
              Zpracování fotografie...
            </Typography>
          )}
        </Box>

        {/* Messages */}
        {error && (
          <Alert
            severity="error"
            onClose={handleClearError}
            sx={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              minWidth: '80%',
              maxWidth: '90%',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            onClose={handleClearSuccess}
            sx={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              minWidth: '80%',
              maxWidth: '90%',
            }}
          >
            {success}
          </Alert>
        )}
      </Box>
    );
  }

  // If PDF has been uploaded, show PDF uploaded interface
  if (showPDFUploaded) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400, // Consistent with main PhotoCapture
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              marginBottom: 2,
            }}
          >
            {isUploading ? 'Odesílání PDF...' : 'PDF nahrán úspěšně'}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              marginBottom: 1,
            }}
          >
            {isUploading 
              ? 'PDF soubor se odesílá pro OCR rozpoznání...' 
              : 'PDF soubor byl nahrán a je připraven k odeslání pro OCR rozpoznání'
            }
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(0,255,0,0.9)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              display: 'block',
              fontWeight: 'bold',
            }}
          >
            {isUploading ? '⏳ Zpracování...' : '✓ Připraveno k odeslání'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          sx={{ width: '100%', maxWidth: 600, marginBottom: 4 }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleProceedWithPDF}
            disabled={isUploading || continuePressed}
            startIcon={isUploading || continuePressed ? <CircularProgress size={24} /> : <CheckIcon />}
            sx={{
              height: 80,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
              flex: 1,
            }}
          >
            {isUploading || continuePressed ? 'Odesílání...' : 'Pokračovat'}
          </Button>
        </Stack>

        {/* Close Button */}
        <IconButton
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Messages */}
        {error && (
          <Alert
            severity="error"
            onClose={handleClearError}
            sx={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              minWidth: '80%',
              maxWidth: '90%',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            onClose={handleClearSuccess}
            sx={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              minWidth: '80%',
              maxWidth: '90%',
            }}
          >
            {success}
          </Alert>
        )}
      </Box>
    );
  }

  // If photo has been captured, show photo captured interface
  if (showPhotoCaptured) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400, // Consistent with main PhotoCapture
          backgroundColor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 3,
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
          <Typography
            variant="h4"
            sx={{
              color: 'white',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              marginBottom: 2,
            }}
          >
            {isUploading ? 'Odesílání fotografie...' : 'Fotografie pořízena úspěšně'}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: 'rgba(255,255,255,0.8)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              marginBottom: 1,
            }}
          >
            {isUploading 
              ? 'Fotografie se odesílá pro OCR rozpoznání...' 
              : 'Fotografie byla pořízena a je připravena k odeslání pro OCR rozpoznání'
            }
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'rgba(0,255,0,0.9)',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
              display: 'block',
              fontWeight: 'bold',
            }}
          >
            {isUploading ? '⏳ Zpracování...' : '✓ Připraveno k odeslání'}
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          sx={{ width: '100%', maxWidth: 600, marginBottom: 4 }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={handleProceedWithPhoto}
            disabled={isUploading || continuePressed}
            startIcon={isUploading || continuePressed ? <CircularProgress size={24} /> : <CheckIcon />}
            sx={{
              height: 80,
              fontSize: '1.2rem',
              fontWeight: 'bold',
              backgroundColor: 'success.main',
              '&:hover': {
                backgroundColor: 'success.dark',
              },
              flex: 1,
            }}
          >
            {isUploading || continuePressed ? 'Odesílání...' : 'Pokračovat'}
          </Button>
        </Stack>

        {/* Close Button */}
        <IconButton
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            top: 20,
            right: 20,
            color: 'white',
            backgroundColor: 'rgba(0,0,0,0.3)',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Messages */}
        {error && (
          <Alert
            severity="error"
            onClose={handleClearError}
            sx={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              minWidth: '80%',
              maxWidth: '90%',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            onClose={handleClearSuccess}
            sx={{
              position: 'absolute',
              top: '20%',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 2,
              minWidth: '80%',
              maxWidth: '90%',
            }}
          >
            {success}
          </Alert>
        )}
      </Box>
    );
  }

  // Main document capture interface
  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1400, // Lower than bottom navigation (2000)
        backgroundColor: 'rgba(0,0,0,0.9)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ textAlign: 'center', marginBottom: 4 }}>
        <Typography
          variant="h4"
          sx={{
            color: 'white',
            fontWeight: 'bold',
            textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
            marginBottom: 2,
          }}
        >
          Nahrát doklad
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'rgba(255,255,255,0.8)',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            marginBottom: 1,
          }}
        >
          Vyberte způsob nahrání dokumentu pro OCR rozpoznání
        </Typography>
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(0,255,0,0.9)',
            textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
            display: 'block',
            fontWeight: 'bold',
          }}
        >
          ✓ Vysoká kvalita pro OCR
        </Typography>
      </Box>

             {/* Document Capture Options */}
       <Stack
         direction={{ xs: 'column', sm: 'row' }}
         spacing={3}
         sx={{ width: '100%', maxWidth: 600, marginBottom: 3 }}
       >
         {/* Camera Option */}
         <Button
           variant="contained"
           size="large"
           onClick={handleCameraClick}
           disabled={isCapturing}
           startIcon={<CameraIcon />}
           sx={{
             height: 120,
             fontSize: '1.2rem',
             fontWeight: 'bold',
             backgroundColor: 'primary.main',
             '&:hover': {
               backgroundColor: 'primary.dark',
             },
             flex: 1,
           }}
         >
                      {isCapturing ? (
             <CircularProgress size={24} />
           ) : (
             'Pořídit foto'
           )}
         </Button>

         {/* PDF Upload Option */}
         <Button
           variant="contained"
           size="large"
           onClick={handlePDFUploadClick}
           disabled={isUploadingPDF}
           startIcon={<PDFIcon />}
           sx={{
             height: 120,
             fontSize: '1.2rem',
             fontWeight: 'bold',
             backgroundColor: 'secondary.main',
             '&:hover': {
               backgroundColor: 'secondary.dark',
             },
             flex: 1,
           }}
         >
           {isUploadingPDF ? (
             <CircularProgress size={24} />
           ) : (
             '📄 Nahrát PDF'
           )}
         </Button>
       </Stack>

       {/* Cancel Button */}
       <Button
         variant="outlined"
         size="large"
         onClick={handleCancel}
         sx={{
           height: 60,
           fontSize: '1.1rem',
           fontWeight: 'bold',
           color: 'white',
           borderColor: 'rgba(255,255,255,0.5)',
           '&:hover': {
             borderColor: 'white',
             backgroundColor: 'rgba(255,255,255,0.1)',
           },
           minWidth: 200,
         }}
       >
         Zrušit
       </Button>

      {/* Hidden file input for PDF upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        onChange={handlePDFUpload}
        style={{ display: 'none' }}
      />

      {/* Close Button */}
      <IconButton
        onClick={handleCancel}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          color: 'white',
          backgroundColor: 'rgba(0,0,0,0.3)',
          '&:hover': {
            backgroundColor: 'rgba(0,0,0,0.5)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Messages */}
      {error && (
        <Alert
          severity="error"
          onClose={handleClearError}
          sx={{
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            minWidth: '80%',
            maxWidth: '90%',
          }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          severity="success"
          onClose={handleClearSuccess}
          sx={{
            position: 'absolute',
            top: '20%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            minWidth: '80%',
            maxWidth: '90%',
          }}
        >
          {success}
        </Alert>
      )}
    </Box>
  );
};

export default PhotoCapture;
